import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { ConceptType, PersonRole } from "@ha/data/basic";
import { SelectionListItem } from "@ha/ui/forms";
import { UntilDestroy, untilDestroyed } from "@ngneat/until-destroy";
import { Actions, ofActionCompleted, ofActionSuccessful, Select, Store } from "@ngxs/store";
import { Observable, Subscription } from "rxjs";

import { getMaximumCertificateExpirationDate, pnrValidator } from "@ha/util/common";
import { PwaState } from "@ha/util/pwa";
import { PermitCreateSelectors } from "../../../permit-create/states/permit-create.selectors";
import { CreateCertificateAndPersonResponse } from "../../models/create-certificate-person.model";
import { PermitTeamActions } from "../../states/permit-team.action";
import { PermitTeamSelectors } from "../../states/permit-team.selectors";
import { PermitTeamSearch } from "../../states/permit-team.state";

@UntilDestroy()
@Component({
    selector: "pmt-create-new-person",
    templateUrl: "create-new-person.component.html",
    styleUrls: ["create-new-person.component.scss"],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CreateNewPersonComponent implements OnInit, OnDestroy {
    public isPremium: boolean;
    public allowExternalCertificates: boolean;
    public ConceptType = ConceptType;
    public searchResults: PermitTeamSearch;

    @Input()
    public permitId: number;

    @Input()
    public role: PersonRole;


    @Select(PermitTeamSelectors.creatingPerson)
    public creatingPerson$: Observable<boolean>;

    @Select(PermitTeamSelectors.newPersonFormResponse)
    public newPersonFormResponse$: Observable<CreateCertificateAndPersonResponse>;

    @ViewChild("validationMessages", { read: ElementRef })
    public validationMessages: ElementRef;

    public form: UntypedFormGroup;

    public createdPersonName: string;

    public maxExpirationDate = getMaximumCertificateExpirationDate();

    public nowDate = new Date().toISOString().split("T")[0];

    public get options(): SelectionListItem[] {
        var options = [{
            value: ConceptType.BrandfarligaArbeten,
            langKey: "permit.team.createPerson.concepts.sweden.brandfarligaArbeten",
        }, {
            value: ConceptType.Denmark,
            langKey: "permit.team.createPerson.concepts.denmark",
        }, {
            value: ConceptType.Finland,
            langKey: "permit.team.createPerson.concepts.finland",
        }, {
            value: ConceptType.Norway,
            langKey: "permit.team.createPerson.concepts.norway",
        }];

        if (this.isPremium && this.allowExternalCertificates) {
            options.push({ value: ConceptType.Other, langKey: "permit.team.createPerson.concepts.other" });
        }

        return options;
    }

    public get title(): string {
        if (this.role == PersonRole.FireWatcher) {
            return "headings.firewatcher";
        } else if (this.role == PersonRole.HotWorker) {
            return "headings.hotworker";
        } else if (this.role == PersonRole.Superintendent) {
            return "headings.superintendent";
        } else if (this.role == PersonRole.PermitIssuer) {
            return "headings.permitissuer";
        } else if (this.role == PersonRole.PostFireWatcher) {
            return "headings.postfirewatcher";
        } else {
            return "error";
        }
    }

    private conceptTypeSubscription: Subscription;
    private hasForeignPersonalNumberSubscription: Subscription;

    constructor(
        private fb: UntypedFormBuilder,
        private store: Store,
        private actions$: Actions,
        private ref: ChangeDetectorRef,
    ) {
    }

    public ngOnInit() {
        this.isPremium = this.store.selectSnapshot(PermitCreateSelectors.isPremium);
        this.allowExternalCertificates = this.store.selectSnapshot(PermitCreateSelectors.allowExternalCertificates);
        this.createForm();
        this.actions$.pipe(ofActionSuccessful(PermitTeamActions.CreateCertificateAndStagePerson)).pipe(
            untilDestroyed(this),
        ).subscribe(() => {
            const response = this.store.selectSnapshot(PermitTeamSelectors.newPersonFormResponse);
            if (response.success) {
                this.createdPersonName = `${this.form.get("firstName").value as string} ${this.form.get("lastName")
                    .value as string}`;
                this.store.dispatch(new PermitTeamActions.ClearCreatePersonFormData()).pipe(untilDestroyed(this))
                    .subscribe((_) => this.form.reset());
            }

            this.ref.detectChanges();
        });
        this.actions$.pipe(ofActionCompleted(PermitTeamActions.CreateCertificateAndStagePerson)).pipe(
            untilDestroyed(this),
        ).subscribe(() => {
            const response = this.store.selectSnapshot(PermitTeamSelectors.newPersonFormResponse);
            const isMobile = this.store.selectSnapshot(PwaState.isMobile);
            if (!response?.success && !isMobile) {
                this.validationMessages.nativeElement.scrollIntoView({ behavior: "smooth", block: "start" });
            }
        });
        this.store.dispatch(new PermitTeamActions.ResetNewPersonResponse());
    }

    public ngOnDestroy(): void {
        if (this.conceptTypeSubscription) {
            this.conceptTypeSubscription.unsubscribe();
        }
        if (this.hasForeignPersonalNumberSubscription) {
            this.hasForeignPersonalNumberSubscription.unsubscribe();
        }
    }

    public createPerson() {
        if (this.form.valid) {
            this.store.dispatch(new PermitTeamActions.CreateCertificateAndStagePerson(this.permitId, this.role));
        } else {
            this.form.markAsDirty();
        }
    }

    public activeConcept() {
        return this.getCurrentActiveConcept();
    }

    public isActiveConcept(concept: ConceptType) {
        return concept == this.getCurrentActiveConcept();
    }

    private createForm() {
        this.createdPersonName = undefined;

        var currentConcept = this.store.selectSnapshot(PermitTeamSelectors.currentConcept);
        var formData = this.store.selectSnapshot(PermitTeamSelectors.newPersonFormData);

        this.form = this.fb.group({
            personId: [formData?.personId ?? 0],
            conceptType: [{ value: currentConcept, disabled: true }, Validators.required],
            certificateIndentifier: [ formData?.certificateIdentifier ?? ""],
            expirationDate: [ formData?.expirationDate ?? "", Validators.required],
            firstName: [{ value: formData?.firstName ?? "", disabled: !!formData?.firstName }, [
                Validators.required,
                Validators.minLength(2),
            ]],
            lastName: [{ value: formData?.lastName ?? "", disabled: !!formData?.lastName }, [
                Validators.required,
                Validators.minLength(2),
            ]],
            email: [{ value: formData?.email ?? "", disabled: !!formData?.email }, [
                Validators.required,
                Validators.pattern("^[^\\s@]+@[^\\s@]+\\.[^\\s@]+$"),
            ]],
            mobile: [{ value: formData?.mobile ?? "", disabled: !!formData?.mobile }, [
                Validators.required,
                Validators.pattern(/^[+]?[\d\s-]+$/),
            ]],
            hasForeignPersonalNumber: [{
                value: formData?.hasForeignPersonalNumber ?? false,
                disabled: !!formData?.hasForeignPersonalNumber || !!formData?.personalNumber || !!formData?.birthDate,
            }, Validators.required],
        });

        if (currentConcept == ConceptType.Other) {
            this.form.get("hasForeignPersonalNumber").setValue(true);
            if (this.form.get("personalNumber") !== null) {
                this.form.removeControl("personalNumber");
            }
            if (this.form.get("birthDate") == null) {
                this.form.addControl(
                    "birthDate",
                    new UntypedFormControl({
                        value: formData?.birthDate ?? "",
                        disabled: !!formData?.birthDate,
                    }, Validators.required),
                );
            }
        }

        if (!this.conceptTypeSubscription) {
            this.conceptTypeSubscription = this.form.get("conceptType").valueChanges.pipe(untilDestroyed(this))
                .subscribe((concept: ConceptType) => {
                    if (
                        concept == ConceptType.BrandfarligaArbeten
                        || concept == ConceptType.Norway
                        || concept == ConceptType.Finland
                        || concept == ConceptType.Other
                    ) {
                        if (this.form.get("certificateIdentifier") == null) {
                            this.form.addControl(
                                "certificateIdentifier",
                                new UntypedFormControl({
                                    value: formData?.certificateIdentifier ?? "",
                                    disabled: !!formData?.certificateIdentifier,
                                }, Validators.required),
                            );
                        }
                    } else if (concept == ConceptType.Denmark) {
                        if (this.form.get("certificateIdentifier") !== null) {
                            this.form.removeControl("certificateIdentifier");
                        }
                    }
                });
        }

        if (!this.hasForeignPersonalNumberSubscription) {
            this.hasForeignPersonalNumberSubscription = this.form.get("hasForeignPersonalNumber").valueChanges.pipe(
                untilDestroyed(this),
            ).subscribe((hasForeignPersonalNumber: boolean) => {
                if (hasForeignPersonalNumber) {
                    if (this.form.get("personalNumber") !== null) {
                        this.form.removeControl("personalNumber");
                    }
                    if (this.form.get("birthDate") == null) {
                        this.form.addControl(
                            "birthDate",
                            new UntypedFormControl({
                                value: formData?.birthDate ?? "",
                                disabled: !!formData?.birthDate,
                            }, Validators.required),
                        );
                    }
                } else {
                    if (this.form.get("birthDate") !== null) {
                        this.form.removeControl("birthDate");
                    }
                    if (this.form.get("personalNumber") == null) {
                        this.form.addControl(
                            "personalNumber",
                            new UntypedFormControl({
                                value: formData?.personalNumber ?? "",
                                disabled: !!formData?.personalNumber,
                            }, [Validators.required, pnrValidator()]),
                        );
                    }
                }
            });
        }
    }

    private getCurrentActiveConcept(): ConceptType {
        if (!this.form || !this.form.get("conceptType").value) {
            return undefined;
        }

        return this.form.get("conceptType").value as ConceptType;
    }
}
